import { useState, useEffect, useCallback } from 'react';
import { ArrowLeft, Upload, FileText, X, Loader2 } from 'lucide-react';
import { supabase } from '../../services/supabaseService';

interface NewProposalProps {
  onBack: () => void;
  onProcessing: (proposalId: string, jobId: string) => void;
}

const ACCEPTED_TYPES = [
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'text/plain',
  'text/markdown',
  'image/png',
  'image/jpeg',
  'image/webp',
];

const ACCEPTED_EXTENSIONS = '.pdf,.docx,.txt,.md,.png,.jpg,.jpeg,.webp';

export function NewProposal({ onBack, onProcessing }: NewProposalProps) {
  const [leads, setLeads] = useState<{ id: string; full_name: string; email: string; company: string }[]>([]);
  const [clients, setClients] = useState<{ id: string; full_name: string; email: string; company: string }[]>([]);
  const [contactId, setContactId] = useState('');
  const [title, setTitle] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [dragOver, setDragOver] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    (async () => {
      // Load leads (separate table)
      const { data: leadsData } = await supabase
        .from('leads')
        .select('id, full_name, email, company')
        .not('status', 'in', '("convertido","descartado")')
        .order('full_name');
      setLeads(leadsData || []);

      // Load clients (contacts table)
      const { data: clientsData } = await supabase
        .from('contacts')
        .select('id, full_name, email, company')
        .eq('is_active', true)
        .order('full_name');
      setClients(clientsData || []);
    })();
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const dropped = Array.from(e.dataTransfer.files).filter(f => ACCEPTED_TYPES.includes(f.type));
    setFiles(prev => [...prev, ...dropped]);
  }, []);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const selected = Array.from(e.target.files).filter(f => ACCEPTED_TYPES.includes(f.type));
      setFiles(prev => [...prev, ...selected]);
    }
  };

  const removeFile = (index: number) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const formatSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const handleProcess = async () => {
    if (files.length === 0) { setError('Sube al menos un archivo'); return; }
    if (!contactId) { setError('Selecciona un lead o cliente'); return; }
    setError('');
    setProcessing(true);

    try {
      // 1. Create proposal
      const { data: user } = await supabase.auth.getUser();
      const [contactType, contactUuid] = contactId.split(':');
      const { data: proposal, error: propError } = await supabase
        .from('proposals')
        .insert({
          title: title || 'Nueva propuesta',
          client_id: contactType === 'client' ? contactUuid : null,
          lead_id: contactType === 'lead' ? contactUuid : null,
          content: {},
          status: 'draft',
          created_by: user.user?.id,
        })
        .select()
        .single();

      if (propError || !proposal) throw new Error(propError?.message || 'Error creando propuesta');

      // 2. Upload files to storage + create source records
      for (const file of files) {
        const storagePath = `${proposal.id}/${file.name}`;
        const { error: uploadError } = await supabase.storage
          .from('proposal-sources')
          .upload(storagePath, file, { contentType: file.type });

        if (uploadError) {
          console.error(`Failed to upload ${file.name}:`, uploadError);
          continue;
        }

        await supabase.from('proposal_sources').insert({
          proposal_id: proposal.id,
          filename: file.name,
          mime_type: file.type,
          storage_path: storagePath,
          size_bytes: file.size,
        });
      }

      // 3. Create ingestion job
      const { data: job, error: jobError } = await supabase
        .from('ingestion_jobs')
        .insert({
          proposal_id: proposal.id,
          status: 'queued',
          progress: 0,
        })
        .select()
        .single();

      if (jobError || !job) throw new Error(jobError?.message || 'Error creando job');

      // 4. Trigger Edge Function
      supabase.functions.invoke('ingest-proposal', {
        body: { proposal_id: proposal.id, job_id: job.id },
      }).catch(err => console.error('Edge function invoke error:', err));

      // 5. Navigate to processing view
      onProcessing(proposal.id, job.id);

    } catch (e: any) {
      setError(e.message || 'Error al procesar');
      setProcessing(false);
    }
  };

  return (
    <div className="p-6 max-w-2xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-3 mb-8">
        <button onClick={onBack} className="p-1.5 rounded hover:bg-gray-100 text-gray-500">
          <ArrowLeft size={18} />
        </button>
        <h1 className="text-2xl font-bold text-gray-900">Nueva propuesta</h1>
      </div>

      {/* Lead / Client selector */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-1">Lead / Cliente *</label>
        <select
          value={contactId}
          onChange={(e) => setContactId(e.target.value)}
          className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm"
        >
          <option value="">Seleccionar...</option>
          {leads.length > 0 && (
            <optgroup label="Leads">
              {leads.map((l) => (
                <option key={`lead-${l.id}`} value={`lead:${l.id}`}>{l.full_name}{l.company ? ` · ${l.company}` : ''}{l.email ? ` (${l.email})` : ''}</option>
              ))}
            </optgroup>
          )}
          {clients.length > 0 && (
            <optgroup label="Clientes">
              {clients.map((c) => (
                <option key={`client-${c.id}`} value={`client:${c.id}`}>{c.full_name}{c.company ? ` · ${c.company}` : ''}{c.email ? ` (${c.email})` : ''}</option>
              ))}
            </optgroup>
          )}
        </select>
      </div>

      {/* Title (optional) */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-1">Título (opcional, la IA lo infiere)</label>
        <input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm"
          placeholder="Se generará automáticamente si lo dejas vacío"
        />
      </div>

      {/* Dropzone */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-2">Documentos *</label>
        <div
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
          className={`border-2 border-dashed rounded-xl p-8 text-center transition-colors cursor-pointer ${
            dragOver ? 'border-blue-400 bg-blue-50' : 'border-gray-300 hover:border-gray-400'
          }`}
          onClick={() => document.getElementById('file-input')?.click()}
        >
          <Upload className="w-10 h-10 text-gray-400 mx-auto mb-3" />
          <p className="text-sm text-gray-600 mb-1">
            Arrastra archivos aquí o <span className="text-blue-600 font-medium">busca en tu equipo</span>
          </p>
          <p className="text-xs text-gray-400">PDF, DOCX, TXT, MD, PNG, JPG — máx. 50MB por archivo</p>
          <input
            id="file-input"
            type="file"
            multiple
            accept={ACCEPTED_EXTENSIONS}
            onChange={handleFileSelect}
            className="hidden"
          />
        </div>
      </div>

      {/* File list */}
      {files.length > 0 && (
        <div className="mb-6 space-y-2">
          {files.map((file, i) => (
            <div key={i} className="flex items-center gap-3 bg-gray-50 rounded-lg px-4 py-2.5 border border-gray-200">
              <FileText size={16} className="text-gray-400 shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm text-gray-700 truncate">{file.name}</p>
                <p className="text-xs text-gray-400">{formatSize(file.size)}</p>
              </div>
              <button onClick={() => removeFile(i)} className="text-gray-400 hover:text-red-500 shrink-0">
                <X size={16} />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Error */}
      {error && <p className="text-sm text-red-600 mb-4">{error}</p>}

      {/* Process button */}
      <button
        onClick={handleProcess}
        disabled={processing || files.length === 0}
        className="w-full flex items-center justify-center gap-2 bg-blue-600 text-white px-6 py-3 rounded-lg text-sm font-semibold hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
      >
        {processing ? (
          <><Loader2 size={16} className="animate-spin" /> Procesando...</>
        ) : (
          <><Upload size={16} /> Procesar con IA</>
        )}
      </button>

      <p className="text-xs text-gray-400 text-center mt-3">
        La IA analizará los documentos y generará una propuesta automáticamente. Podrás revisarla antes de publicarla.
      </p>
    </div>
  );
}
